import { useState } from "react";
import { api } from "../app/api";
import { useAsync } from "../app/useAsync";
import { StatusChip } from "../components/StatusChip";

/**
 * Controlled single-write test harness (Phase 4C).
 * Creates ONE sandlot_ruck_club metaobject, gated by approval + the write flag.
 */
export function MigrationTestingPage() {
  const writes = useAsync(api.writesStatus, []);
  const schema = useAsync(api.schemaValidate, []);
  const [sandlotId, setSandlotId] = useState("");
  const [log, setLog] = useState<string[]>([]);
  const [preview, setPreview] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const append = (line: string) => setLog((l) => [...l, line]);

  async function run(label: string, fn: () => Promise<unknown>) {
    const id = sandlotId.trim();
    if (!id) {
      append("⚠ Enter a Sandlot club group_id first.");
      return;
    }
    setBusy(label);
    append(`▶ ${label}…`);
    try {
      const res = await fn();
      append(`   ${JSON.stringify(res)}`);
      return res;
    } catch (e) {
      append(`   ✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <h1>Migration Testing</h1>
      <p className="page-sub">
        Controlled single-object write test for one <code>sandlot_ruck_club</code>.
        Gated by approval and the write flag; idempotent by handle.
      </p>

      <div className="grid" style={{ marginBottom: "1.2rem" }}>
        <div className="card">
          <h3>Entity</h3>
          <div className="stat" style={{ fontSize: "1.2rem" }}>Ruck Club</div>
          <p className="muted">sandlot_ruck_club</p>
        </div>
        <div className="card">
          <h3>Schema</h3>
          <div style={{ marginTop: 6 }}>
            <StatusChip
              tone={schema.data?.summary?.overall === "PASS" ? "ok" : "warn"}
              label={schema.loading ? "…" : (schema.data?.summary?.overall ?? "unknown")}
            />
          </div>
        </div>
        <div className="card">
          <h3>Approval</h3>
          <div style={{ marginTop: 6 }}>
            <StatusChip tone="warn" label="Required" />
          </div>
        </div>
        <div className="card">
          <h3>Write</h3>
          <div style={{ marginTop: 6 }}>
            <StatusChip
              tone={writes.data?.writesEnabled ? "ok" : "warn"}
              label={writes.loading ? "…" : writes.data?.writesEnabled ? "Enabled" : "Disabled"}
            />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: "1.2rem" }}>
        <h3>Test club</h3>
        <input
          value={sandlotId}
          onChange={(e) => setSandlotId(e.target.value)}
          placeholder="Sandlot club group_id (UUID)…"
          style={{
            width: "100%",
            padding: "0.5rem 0.7rem",
            background: "var(--surface)",
            border: "1px solid var(--hair)",
            borderRadius: 8,
            color: "var(--ink)",
            marginBottom: 12,
          }}
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={btn} disabled={!!busy} onClick={() => run("Generate Preview", async () => {
            const r = await api.buildClubPreview(sandlotId.trim());
            setPreview(r.preview ?? null);
            return { handle: r.preview?.handle, warnings: r.preview?.warnings, error: r.error };
          })}>Generate Preview</button>
          <button style={btn} disabled={!!busy} onClick={() => run("Request Approval", () => api.requestApproval(sandlotId.trim()))}>Request Approval</button>
          <button style={{ ...btn, background: "var(--accent)" }} disabled={!!busy} onClick={() => run("Execute Test Write", () => api.executeClubWrite(sandlotId.trim()))}>Execute Test Write</button>
          <button style={btn} disabled={!!busy} onClick={() => run("Verify Idempotency (re-run write)", () => api.executeClubWrite(sandlotId.trim()))}>Verify Idempotency</button>
          <button style={{ ...btn, background: "var(--surface-2)", color: "var(--ink)" }} disabled={!!busy} onClick={() => run("Rollback Preview", () => api.rollbackPreview(sandlotId.trim()))}>Rollback Preview</button>
        </div>
      </div>

      {preview !== null && (
        <div className="card" style={{ marginBottom: "1.2rem" }}>
          <h3>Preview payload</h3>
          <pre style={pre}>{JSON.stringify(preview, null, 2)}</pre>
        </div>
      )}

      <div className="card">
        <h3>Activity log</h3>
        {log.length === 0 ? (
          <p className="muted">No actions yet.</p>
        ) : (
          <pre style={pre}>{log.join("\n")}</pre>
        )}
      </div>
    </>
  );
}

const btn: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "var(--surface-2)",
  color: "var(--ink)",
  border: "1px solid var(--hair)",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
};
const pre: React.CSSProperties = {
  overflowX: "auto",
  fontSize: "0.78rem",
  background: "var(--surface-2)",
  padding: "0.9rem",
  borderRadius: 8,
};
